import type { GlossaryKey } from '../content/glossary';

export type StepState = 'done' | 'current' | 'upcoming';

export type GettingStartedStepKey = 'org' | 'project' | 'verify' | 'scan' | 'journeys' | 'export';

export interface GettingStartedStep {
  readonly key: GettingStartedStepKey;
  readonly title: string;
  readonly description: string;
  readonly state: StepState;
  /** Glossary term for an inline HelpTip beside the title, if any. */
  readonly helpTerm?: GlossaryKey;
  /** Present ONLY on the current step. */
  readonly cta?: { readonly label: string; readonly to: string };
  /** Present on a DONE step that has a page worth revisiting (e.g. "View scans"). */
  readonly link?: { readonly label: string; readonly to: string };
}

export interface GettingStartedInput {
  /** Defaults to true (Home is only reachable with an org). */
  readonly hasOrg?: boolean;
  readonly hasProject: boolean;
  readonly anyVerified: boolean;
  readonly anyScanned: boolean;
  readonly anyJourneys: boolean;
  readonly anyTestCases: boolean;
  /** The project to act on for steps 3–6 (e.g. the first unverified/first project). */
  readonly targetProjectId: string | null;
}

export interface DerivedGettingStarted {
  readonly steps: GettingStartedStep[];
  readonly complete: boolean;
  /** 1-based number of the current step, or null when complete. */
  readonly currentStepNumber: number | null;
}

interface StepMeta {
  readonly key: GettingStartedStepKey;
  readonly title: string;
  readonly description: string;
  /** Shown instead of `description` while the step is not yet done. */
  readonly pendingDescription?: string;
  readonly helpTerm?: GlossaryKey;
  readonly ctaLabel: string;
  /** Label for the persistent "revisit" link shown once the step is done. */
  readonly viewLabel?: string;
  readonly to: (projectId: string | null) => string | null;
}

const STEP_META: readonly StepMeta[] = [
  {
    key: 'org',
    title: 'Create your organization',
    description: 'Done — your workspace is ready.',
    pendingDescription: 'Set up a workspace for your team, projects, and results.',
    ctaLabel: 'Create your organization',
    to: () => '/create-organization',
  },
  {
    key: 'project',
    title: 'Create a project',
    description: "Tell us the storefront you'd like us to watch. It takes about a minute.",
    ctaLabel: 'Create a project',
    to: () => '/projects/new',
  },
  {
    key: 'verify',
    title: 'Verify your store domain',
    description: 'Prove the store is yours with a quick one-time check, so only you can point us at it.',
    helpTerm: 'domainVerification',
    ctaLabel: 'Verify domain',
    viewLabel: 'View domain status',
    to: (id) => (id ? `/projects/${id}/domain-verification` : null),
  },
  {
    key: 'scan',
    title: 'Run your first scan',
    description:
      "We'll browse your store like a shopper and check that key paths work — without buying or changing anything.",
    helpTerm: 'scan',
    ctaLabel: 'Run a scan',
    viewLabel: 'View scans',
    to: (id) => (id ? `/projects/${id}/scans` : null),
  },
  {
    key: 'journeys',
    title: 'Review your customer journeys',
    description: 'See the shopper paths we found and how each one is doing.',
    helpTerm: 'journey',
    ctaLabel: 'View journeys',
    viewLabel: 'View journeys',
    to: (id) => (id ? `/projects/${id}/journey-health` : null),
  },
  {
    key: 'export',
    title: 'Export your tests',
    description:
      'Turn your journeys into ready-to-run test scripts (Playwright, Cypress, Selenium, or Gherkin) you can download and keep.',
    helpTerm: 'testCases',
    ctaLabel: 'Export tests',
    viewLabel: 'View tests',
    to: (id) => (id ? `/projects/${id}/test-cases` : null),
  },
];

/**
 * Derive the getting-started checklist state from already-loaded data (pure, no
 * I/O). Exactly one not-yet-done step is `current`; everything after it is
 * `upcoming`. A step is only `done` on a real signal — never falsely marked.
 */
export function deriveGettingStarted(input: GettingStartedInput): DerivedGettingStarted {
  const done: Record<GettingStartedStepKey, boolean> = {
    org: input.hasOrg ?? true,
    project: input.hasProject,
    verify: input.anyVerified,
    scan: input.anyScanned,
    journeys: input.anyJourneys,
    export: input.anyTestCases,
  };

  const firstIncomplete = STEP_META.findIndex((m) => !done[m.key]);
  const complete = firstIncomplete === -1;

  const steps = STEP_META.map((meta, index): GettingStartedStep => {
    const state: StepState = done[meta.key]
      ? 'done'
      : index === firstIncomplete
        ? 'current'
        : 'upcoming';
    const target = meta.to(input.targetProjectId);
    const cta =
      state === 'current' && target ? { label: meta.ctaLabel, to: target } : undefined;
    const link =
      state === 'done' && target && meta.viewLabel
        ? { label: meta.viewLabel, to: target }
        : undefined;
    return {
      key: meta.key,
      title: meta.title,
      description:
        state !== 'done' && meta.pendingDescription ? meta.pendingDescription : meta.description,
      state,
      ...(meta.helpTerm ? { helpTerm: meta.helpTerm } : {}),
      ...(cta ? { cta } : {}),
      ...(link ? { link } : {}),
    };
  });

  return {
    steps,
    complete,
    currentStepNumber: complete ? null : firstIncomplete + 1,
  };
}
